import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface AllowedOptionsSelectProps<T extends string> {
  options: { value: T; label: string }[];
  value: T[] | null;
  onChange: (value: T[] | null) => void;
  disabled?: boolean;
}

/**
 * Checklist for a setting that limits a value to selected options: null means all are allowed.
 * @param props - AllowedOptionsSelectProps
 * @returns Options select element
 */
export function AllowedOptionsSelect<T extends string>({
  options,
  value,
  onChange,
  disabled,
}: AllowedOptionsSelectProps<T>) {
  const selected = value ?? options.map((option) => option.value);

  /**
   * Toggles one option, keeping at least one selected and storing all as null.
   * @param option - The option being toggled
   */
  const handleToggle = (option: T) => {
    const next = selected.includes(option)
      ? selected.filter((entry) => entry !== option)
      : [...selected, option];
    if (next.length === 0) {
      return;
    }
    if (next.length === options.length) {
      onChange(null);
      return;
    }
    onChange(options.map((o) => o.value).filter((v) => next.includes(v)));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button
          variant="outline"
          className="h-auto max-w-64 justify-between gap-2 whitespace-normal text-left"
        >
          <span>{describeSelection(options, value)}</span>
          <ChevronDown className="h-4 w-4 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto">
        {options.map((option) => (
          <DropdownMenuCheckboxItem
            key={option.value}
            checked={selected.includes(option.value)}
            onCheckedChange={() => handleToggle(option.value)}
            onSelect={(e) => e.preventDefault()}
          >
            {option.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Summarizes the selected options for the trigger button.
 * @param options - Every option with its label
 * @param value - The selected options, or null when all are allowed
 * @returns "All" or the selected labels joined by commas
 */
function describeSelection<T extends string>(
  options: { value: T; label: string }[],
  value: T[] | null,
): string {
  if (value === null) {
    return "All";
  }
  return options
    .filter((option) => value.includes(option.value))
    .map((option) => option.label)
    .join(", ");
}
